import { useRef, useCallback, useEffect } from 'react';

interface UseTouchHandScrollOptions {
    containerRef: React.RefObject<HTMLElement>;
    onScrollUpdate: (delta: number) => void;
    enabled?: boolean;
}

/**
 * Hook to enable horizontal swipe-to-scroll on the hand container for touch devices.
 * This replaces the hover-based HandScroller zones on mobile.
 */
export function useTouchHandScroll(options: UseTouchHandScrollOptions) {
    const { containerRef, onScrollUpdate, enabled = true } = options;

    const startXRef = useRef<number | null>(null);
    const lastXRef = useRef<number | null>(null);
    const activePointerRef = useRef<number | null>(null);

    const handlePointerDown = useCallback((e: PointerEvent) => {
        if (!enabled) return;
        // Only track if starting within the container
        if (!containerRef.current?.contains(e.target as Node)) return;

        activePointerRef.current = e.pointerId;
        startXRef.current = e.clientX;
        lastXRef.current = e.clientX;
    }, [enabled, containerRef]);

    const handlePointerMove = useCallback((e: PointerEvent) => {
        if (!enabled) return;
        if (activePointerRef.current !== e.pointerId) return;
        if (lastXRef.current === null) return;

        const delta = e.clientX - lastXRef.current;
        lastXRef.current = e.clientX;

        // Pass normalized delta (negative = scroll left, positive = scroll right)
        onScrollUpdate(delta);
    }, [enabled, onScrollUpdate]);

    const handlePointerUp = useCallback((e: PointerEvent) => {
        if (activePointerRef.current === e.pointerId) {
            activePointerRef.current = null;
            startXRef.current = null;
            lastXRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const container = containerRef.current;
        if (!container) return;

        container.addEventListener('pointerdown', handlePointerDown);
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);

        return () => {
            container.removeEventListener('pointerdown', handlePointerDown);
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
        };
    }, [enabled, containerRef, handlePointerDown, handlePointerMove, handlePointerUp]);
}
